/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';

/**
 * Nahrazovač pro autoreboot, který provádí synchronizaci časovače automatického
 * restartu zařízení, dle nastavení v obsahu zařízení.
 */
class AutorebootReplacer {
  /**
   * @param {boolean} defaultDisabled Výchozí hodnota deaktivace
   * preventivní restartu.
   * @param {number} defaultInterval Výchozí interval, ve kterém se zařízení
   * preventivně restartuje. [ms]
   * @param {AutorebootManager} manager
   */
  constructor(defaultDisabled, defaultInterval, manager) {
    this.defaultDisabled = defaultDisabled;
    this.defaultInterval = defaultInterval;
    this.manager = manager;
  }

  /**
   * @param {number} [interval] Nastavení intervalu preventivní restartu
   * z obsahu zařízení.
   * @private
   * @returns {number}
   */
  getInterval(interval) {
    if (isNil(interval)) {
      return this.defaultInterval;
    }
    return interval;
  }

  /**
   * @param {boolean} [disabled] Nastavení deaktivace preventivní restartu
   * z obsahu zařízení.
   * @private
   * @returns {boolean}
   */
  isDisabled(disabled) {
    if (isNil(disabled)) {
      return this.defaultDisabled;
    }
    return disabled;
  }

  /**
   * Nastaví časovače dle obsahu zařízení.
   * @param {ContentInfo} contentInfo
   * @returns {Promise.<undefined>}
   */
  replace(contentInfo) {
    return new Promise((resolve) => {
      const { manager } = this;
      const { session } = contentInfo;
      const disabled = this.isDisabled(session.autorebootDisabled);
      if (disabled) {
        // Pokud je autoreboot zakázaný, pokuze se zastaví časovač.
        manager.stop();
        return resolve();
      }
      const interval = this.getInterval(session.autorebootInterval);
      if (!manager.isStarted()) {
        // Pokud je autoreboot vypnutý, zapne se s požadovaným intervalem.
        manager.start(interval);
        return resolve();
      }
      const currentInterval = manager.getInterval();
      if (interval === currentInterval) {
        // Pokud je autoreboot zapnutý a požadovaný interval se nezměnil,
        // nic se nedělá a nechá se dál běžet.
        return resolve();
      }
      // Interval se změnil, časovač se restartuje s novým intervalem.
      manager.stop();
      manager.start(interval);
      return resolve();
    });
  }
}

export default AutorebootReplacer;
